<template>
  <div class="language-selector" :class="{ open: open, 'main-theme': main, 'dark-theme': !main }" @click="open = !open">
    <div class="value">
      <div class="flag" :class="`flag-${userLanguage}`"></div>
      <span>{{ userLanguage }}</span>
      <div class="arrow"></div>
    </div>

    <div class="dropdown-mask">
      <Transition name="dropdown">
        <div v-if="open" class="dropdown">
          <div class="content">
            <div
              v-for="language in languages"
              :key="language"
              class="language-value value"
              :class="{ active: userLanguage && userLanguage.toLowerCase() === language }"
              @click="selectLang(language)"
            >
              <div class="flag" :class="`flag-${language}`"></div>
              <span>{{ language }}</span>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useSignalR } from '@dreamonkey/vue-signalr';

export default {
  name: 'LanguageSelector',

  props: {
    main: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      open: false,
      languages: ['fr', 'en', 'de'],
    };
  },

  computed: {
    ...mapGetters({
      userLanguage: 'userLanguage',
    }),
  },

  created() {
    this.$socket = useSignalR();
  },

  methods: {
    selectLang(lang) {
      if (!this.isDesktop) {
        this.$socket.invoke('action', { action: 'selectLang', parameters: lang });
      }
      this.$store.dispatch('changeLanguage', lang);
      this.$i18n.locale = lang;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/animations.scss';

.language-selector.main-theme {
  position: relative;
  height: 100%;
  width: 128px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0 24px;

  border-left: solid 1px transparent;
  border-right: solid 1px transparent;

  transition: 0.2s ease-out;

  &.open {
    border-left-color: colors.$clear-border-color;
    border-right-color: colors.$clear-border-color;
  }

  .value {
    display: flex;
    align-items: center;
    text-transform: uppercase;
    cursor: pointer;
    transition: 0.2s ease-out;

    .flag {
      width: 24px;
      height: 24px;
      margin-right: 8px;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;

      &.flag-en {
        background-image: url('./../../assets/flags/en.png');
      }

      &.flag-de {
        background-image: url('./../../assets/flags/de.png');
      }

      &.flag-fr {
        background-image: url('./../../assets/flags/fr.png');
      }
    }

    .arrow {
      $size: 6px;
      width: $size;
      height: $size;
      transform: rotate(45deg);
      border-right: 2px solid colors.$stone-color;
      border-bottom: 2px solid colors.$stone-color;
      border-radius: 1px;
      margin: -2px 4px 2px 12px;
    }

    &:hover,
    &.open {
      color: colors.$stone-color;
    }
  }

  .dropdown-mask {
    position: absolute;
    z-index: 1000;
    top: 100%;
    bottom: -192px;
    left: -25px;
    right: -25px;

    overflow-x: visible;
    overflow-y: hidden;
    pointer-events: none;

    .dropdown {
      position: absolute;
      z-index: 800;
      left: 24px;
      right: 24px;
      top: 0;

      background: colors.$white-color;
      border: 1px solid colors.$clear-border-color;
      border-top: none;
      border-radius: 0 0 2px 2px;
      box-sizing: border-box;
      overflow: hidden;
      pointer-events: all;
      cursor: default;
      box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.4);
      transition: all 0.4s ease;

      .content {
        padding: 4px 24px 16px;
        cursor: default;

        .button {
          color: #262631;
          font-size: 14px;
          margin: 0 -24px;
          padding: 8px 24px;
          cursor: pointer;
          transition: all 0.4s ease;

          &:hover {
            color: colors.$main-color;
            opacity: 1;
          }

          &.delete-account {
            font-size: 10px;
            opacity: 0.6;
          }
        }

        .language-value {
          padding: 4px 0;

          &.active {
            font-weight: bold;
          }

          &:hover {
            color: colors.$main-color;
          }
        }
      }
    }
  }

  .dropdown-mask--old {
    position: absolute;
    z-index: 1000;
    top: 49px;
    right: 0;
    bottom: 0;
    left: 0;
    overflow-x: visible;
    overflow-y: hidden;
    pointer-events: none;

    .dropdown {
      position: absolute;
      z-index: 800;
      right: 0;
      // right: 24px;
      width: 192px;
      background: colors.$white-color;
      border: 1px solid colors.$clear-border-color;
      border-top: none;
      border-radius: 0 0 2px 2px;
      box-sizing: border-box;
      overflow: hidden;
      pointer-events: all;
      cursor: default;
      box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.4);
      transition: all 0.4s ease;

      .content {
        padding: 4px 24px 16px;
        cursor: default;

        .button {
          color: #262631;
          font-size: 14px;
          margin: 0 -24px;
          padding: 8px 24px;
          cursor: pointer;
          transition: all 0.4s ease;

          &:hover {
            color: colors.$main-color;
            opacity: 1;
          }

          &.delete-account {
            font-size: 10px;
            opacity: 0.6;
          }
        }

        .language-selector {
          display: flex;
          width: 96px;
          margin: 12px auto 0;

          .language-value {
            width: 48px;
            height: 32px;
            line-height: 32px;
            font-size: 14px;
            text-align: center;
            border: solid 1px colors.$clear-border-color;
            border-right: none;
            transition: all 0.4s ease;

            &:first-child {
              border-radius: 4px 0 0 4px;
            }

            &:last-child {
              border-right: solid 1px colors.$clear-border-color;
              border-radius: 0 4px 4px 0;
            }

            &:not(.active) {
              color: #626468;
              cursor: pointer;

              &:hover {
                background: rgba(colors.$clear-border-color, 0.2);
              }
            }

            &.active {
              background: colors.$clear-border-color;
              color: #262631;
            }
          }
        }
      }
    }
  }
}

.language-selector.dark-theme {
  width: 128px;
  display: flex;
  align-items: center;
  box-sizing: border-box;

  position: absolute;
  height: 48px;
  right: 32px;
  top: 16px;
  color: colors.$stone-color;

  transition: 0.2s ease-out;

  &.open {
  }

  .value {
    position: relative;
    z-index: 100;

    display: flex;
    align-items: center;
    text-transform: uppercase;
    cursor: pointer;
    transition: 0.2s ease-out;

    &:not(.language-value) {
      width: 100%;
      height: 100%;
      padding: 0 24px;
      border-radius: 32px;
      background: colors.$white-color;
      box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.4);
    }

    .flag {
      width: 24px;
      height: 24px;
      margin-right: 8px;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;

      &.flag-en {
        background-image: url('./../../assets/flags/en.png');
      }

      &.flag-fr {
        background-image: url('./../../assets/flags/fr.png');
      }
    }

    .arrow {
      $size: 6px;
      width: $size;
      height: $size;
      transform: rotate(45deg);
      border-right: 2px solid colors.$stone-color;
      border-bottom: 2px solid colors.$stone-color;
      border-radius: 1px;
      margin: -2px 4px 2px 12px;
    }

    &:hover,
    &.open {
      color: colors.$stone-color;
    }
  }

  .dropdown-mask {
    position: absolute;
    z-index: 1000;
    top: calc(100% - 24px);
    bottom: -192px;
    left: -25px;
    right: -25px;

    z-index: 10;

    overflow-x: visible;
    overflow-y: hidden;
    pointer-events: none;

    .dropdown {
      position: absolute;
      z-index: 800;
      left: 32px;
      right: 32px;
      top: 0;

      background: colors.$white-color;
      border: 1px solid colors.$clear-border-color;
      border-top: none;
      border-radius: 0 0 2px 2px;
      box-sizing: border-box;
      overflow: hidden;
      pointer-events: all;
      cursor: default;
      box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.4);
      transition: all 0.4s ease;

      .content {
        padding: 28px 24px 12px;
        cursor: default;

        .button {
          color: #262631;
          font-size: 14px;
          margin: 0 -24px;
          padding: 8px 24px;
          cursor: pointer;
          transition: all 0.4s ease;

          &:hover {
            color: colors.$main-color;
            opacity: 1;
          }

          &.delete-account {
            font-size: 10px;
            opacity: 0.6;
          }
        }

        .language-value {
          padding: 4px 0;

          &.active {
            font-weight: bold;
          }

          &:hover {
            color: colors.$main-color;
          }
        }
      }
    }
  }

  .dropdown-mask--old {
    position: absolute;
    z-index: 1000;
    top: 49px;
    right: 0;
    bottom: 0;
    left: 0;
    overflow-x: visible;
    overflow-y: hidden;
    pointer-events: none;

    .dropdown {
      position: absolute;
      z-index: 800;
      right: 0;
      // right: 24px;
      width: 192px;
      background: colors.$white-color;
      border: 1px solid colors.$clear-border-color;
      border-top: none;
      border-radius: 0 0 2px 2px;
      box-sizing: border-box;
      overflow: hidden;
      pointer-events: all;
      cursor: default;
      box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.4);
      transition: all 0.4s ease;

      .content {
        padding: 4px 24px 16px;
        cursor: default;

        .button {
          color: #262631;
          font-size: 14px;
          margin: 0 -24px;
          padding: 8px 24px;
          cursor: pointer;
          transition: all 0.4s ease;

          &:hover {
            color: colors.$main-color;
            opacity: 1;
          }

          &.delete-account {
            font-size: 10px;
            opacity: 0.6;
          }
        }

        .language-selector {
          display: flex;
          width: 96px;
          margin: 12px auto 0;

          .language-value {
            width: 48px;
            height: 32px;
            line-height: 32px;
            font-size: 14px;
            text-align: center;
            border: solid 1px colors.$clear-border-color;
            border-right: none;
            transition: all 0.4s ease;

            &:first-child {
              border-radius: 4px 0 0 4px;
            }

            &:last-child {
              border-right: solid 1px colors.$clear-border-color;
              border-radius: 0 4px 4px 0;
            }

            &:not(.active) {
              color: #626468;
              cursor: pointer;

              &:hover {
                background: rgba(colors.$clear-border-color, 0.2);
              }
            }

            &.active {
              background: colors.$clear-border-color;
              color: #262631;
            }
          }
        }
      }
    }
  }
}
</style>
